/**
 * RuntimeCard — how responsive otr-core was over the last hour (Health tab).
 *
 * The bot, the API and the background workers share one thread. When a
 * worker holds it (a pipeline batch parsing scraped HTML froze it for 2–6 s
 * every 5 minutes on 1 Nov 2026), nobody's bot tap or API call can even
 * start. The bars are the longest such wait in each minute; red is a wait
 * people feel (≥ 1 s). Below: the bot's own updates and the slowest ones.
 */

import { useQuery } from '@tanstack/react-query';
import { getRuntime } from '@/api/observe';
import ChartCard from '@/components/ChartCard';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';
import type { RuntimeSnapshot } from '@/types';

/** A bot update slower than this is a bad experience (a /deals should take ~2 s). */
const SLOW_UPDATE_MS = 5_000;

const fmtMs = (ms: number | null | undefined) =>
  ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;

export function summarise(rt: RuntimeSnapshot) {
  const worst = rt.loop.reduce((m, x) => Math.max(m, x.maxMs), 0);
  const stallMinutes = rt.loop.filter(x => x.maxMs >= rt.stallMs).length;
  const updates = rt.bot.reduce((n, x) => n + x.updates, 0);
  const errors = rt.bot.reduce((n, x) => n + x.errors, 0);
  const timeouts = rt.bot.reduce((n, x) => n + x.timeouts, 0);
  const dropped = rt.bot.reduce((n, x) => n + (x.dropped ?? 0), 0);
  const p95s = rt.bot.map(x => x.p95Ms).filter((v): v is number => v != null);
  const worstP95 = p95s.length ? Math.max(...p95s) : null;
  const slowest = rt.bot.flatMap(x => x.slowest.map(s => ({ ...s, at: x.at })))
    .sort((a, b) => b.ms - a.ms)
    .slice(0, 5);
  return { worst, stallMinutes, updates, errors, timeouts, dropped, worstP95, slowest };
}

export default function RuntimeCard() {
  const q = useQuery({ queryKey: ['runtime'], queryFn: getRuntime, refetchInterval: 30_000 });
  const rt = q.data?.runtime;
  const s = rt ? summarise(rt) : null;

  return (
    <ChartCard
      title="Bot & API responsiveness — last hour"
      subtitle="Longest wait per minute before anything could run (red ≥ 1 s), and the bot's own updates. Fills in minute by minute after a restart."
      isLoading={q.isLoading}
      isError={q.isError}
      isEmpty={!!rt && rt.loop.length === 0}
      emptyLabel="No full minute recorded yet since the last restart."
    >
      {rt && s && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Worst freeze" value={fmtMs(s.worst)} bad={s.worst >= rt.stallMs}
              note={rt.lastStallAt ? `last ≥ 1 s ${relativeTime(rt.lastStallAt)}` : 'none ≥ 1 s'} />
            <Stat label="Minutes with a freeze ≥ 1 s" value={String(s.stallMinutes)} bad={s.stallMinutes > 0}
              note={`of ${rt.loop.length}`} />
            <Stat label="Bot updates" value={String(s.updates)} bad={s.errors + s.timeouts > 0}
              note={`${s.errors} failed · ${s.timeouts} timed out${s.dropped ? ` · ${s.dropped} dropped` : ''}`} />
            <Stat label="Bot p95 per minute (worst)" value={fmtMs(s.worstP95)} bad={(s.worstP95 ?? 0) >= SLOW_UPDATE_MS}
              note="start to last card sent" />
          </div>

          <StallBars rt={rt} />

          {!!rt.dealsRuns?.length && <DealsRuns runs={rt.dealsRuns.slice(0, 8)} />}

          {s.slowest.length > 0 && (
            <div>
              <div className="text-xs text-slate-500 mb-1">Slowest bot updates</div>
              <ul className="divide-y divide-slate-800/60">
                {s.slowest.map((x, i) => (
                  <li key={i} className="py-1 flex items-baseline justify-between gap-3 text-xs">
                    <span className="font-mono text-slate-300 truncate">{x.kind}</span>
                    <span className="text-slate-500 shrink-0">{relativeTime(x.at)}</span>
                    <span className={cn('font-mono shrink-0', x.ms >= SLOW_UPDATE_MS ? 'text-red-300' : 'text-slate-300')}>{fmtMs(x.ms)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </ChartCard>
  );
}

/** Where each recent /deals spent its time. Red: slow overall, or a card went without its photo or at all. */
function DealsRuns({ runs }: { runs: NonNullable<RuntimeSnapshot['dealsRuns']> }) {
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1">Recent /deals, newest first</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500">
              <th className="font-normal pr-3 py-1">When</th>
              <th className="font-normal pr-3 py-1 text-right">Total</th>
              <th className="font-normal pr-3 py-1 text-right">Load</th>
              <th className="font-normal pr-3 py-1 text-right">Photo wait</th>
              <th className="font-normal pr-3 py-1 text-right">Sending</th>
              <th className="font-normal pr-3 py-1 text-right">Telegram limit</th>
              <th className="font-normal py-1 text-right">Photos</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60 font-mono text-slate-300">
            {runs.map(r => (
              <tr key={r.at}>
                <td className="pr-3 py-1 font-sans text-slate-500 whitespace-nowrap">{relativeTime(r.at)}</td>
                <td className={cn('pr-3 py-1 text-right', r.totalMs >= SLOW_UPDATE_MS && 'text-red-300')}>{fmtMs(r.totalMs)}</td>
                <td className="pr-3 py-1 text-right">{fmtMs(r.loadMs)}</td>
                <td className="pr-3 py-1 text-right">{fmtMs(r.photoWaitMs)}</td>
                <td className="pr-3 py-1 text-right">{fmtMs(r.sendMs)}</td>
                <td className={cn('pr-3 py-1 text-right', r.floodWaitS > 0 && 'text-amber-300')}>{r.floodWaitS} s</td>
                <td className={cn('py-1 text-right', (r.photos < r.cards || r.failed > 0) && 'text-red-300')}>{r.photos}/{r.cards}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, note, bad }: { label: string; value: string; note?: string; bad?: boolean }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/40 p-3 min-w-0">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={cn('mt-1 text-lg font-semibold', bad ? 'text-red-300' : 'text-emerald-300')}>{value}</div>
      {note && <div className="text-[11px] text-slate-500 truncate">{note}</div>}
    </div>
  );
}

/** One bar per minute: the longest event-loop wait. Capped at 6 s so one huge stall does not flatten the rest. */
function StallBars({ rt }: { rt: RuntimeSnapshot }) {
  const height = 64;
  const cap = 6_000;
  const n = Math.max(rt.windowMinutes, rt.loop.length);
  const barW = 100 / n;
  const offset = n - rt.loop.length; // newest minute on the right
  const lineY = height - (rt.stallMs / cap) * (height - 2) - 1;
  return (
    <div>
      <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }}
        role="img" aria-label="Longest event-loop wait per minute, last hour">
        <line x1={0} x2={100} y1={lineY} y2={lineY} stroke="#64748b" strokeDasharray="1 1" strokeWidth={0.3} />
        {rt.loop.map((m, i) => {
          const h = Math.max(1, (Math.min(m.maxMs, cap) / cap) * (height - 2));
          return (
            <rect key={m.at} x={(offset + i) * barW + barW * 0.1} y={height - h - 1} width={barW * 0.8} height={h}
              fill={m.maxMs >= rt.stallMs ? '#f87171' : '#34d399'}>
              <title>{`${new Date(m.at).toLocaleTimeString()} — longest wait ${fmtMs(m.maxMs)}`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-600 mt-0.5">
        <span>60 min ago</span><span>dashed line = 1 s</span><span>now</span>
      </div>
    </div>
  );
}
